import { useEffect, useRef } from 'react';

/**
 * One pointer gesture: window-level move/up listeners while `active`, plus the
 * page-wide cursor and selection lock every drag needs so the gesture does not
 * smear a text selection across whatever it passes over.
 *
 * Window-level rather than pointer capture on the handle, because a drag
 * spends its whole life off the few pixels it started on, and a release
 * outside the element (or outside the window) still has to end the gesture.
 */
export function usePointerSession(
  active: boolean,
  cursor: 'grabbing' | 'col-resize',
  onMove: (event: PointerEvent) => void,
  onEnd: (event: PointerEvent) => void,
) {
  // Listeners are installed once per gesture, so they would otherwise close
  // over the callbacks as they were when it began.
  const handlers = useRef({ onMove, onEnd });
  handlers.current = { onMove, onEnd };

  useEffect(() => {
    if (!active) return;
    const move = (event: PointerEvent) => handlers.current.onMove(event);
    const end = (event: PointerEvent) => handlers.current.onEnd(event);
    const body = document.body;
    const priorCursor = body.style.cursor;
    const priorSelect = body.style.userSelect;
    body.style.cursor = cursor;
    body.style.userSelect = 'none';
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
    return () => {
      body.style.cursor = priorCursor;
      body.style.userSelect = priorSelect;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };
  }, [active, cursor]);
}
